import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBadgeCheck,
  LuCalendarDays,
  LuCalendarRange,
  LuCoins,
  LuFileText,
  LuGlobe,
  LuHash,
  LuHeadset,
  LuIdCard,
  LuMapPin,
  LuPencil,
  LuToggleLeft,
  LuToggleRight,
  LuUsers,
  LuWallet,
} from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import DetailPane from '../../../components/data/DetailPane';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  displayValue,
  formatIsoDate,
  formatPaymentPeriod,
  getFilterValueLabel,
  isActiveCustomer,
  isEmptyValue,
} from '../customerColumns';
import { StatusBadge, customerInitials } from './CustomerCell';

const text = (value) => (isEmptyValue(value) ? '' : String(value));

/**
 * The selected customer's panel: identity, the actions of the old action bar
 * (card, edit, addresses, contacts), key details and the status switch.
 * Docked beside the table on wide screens, a drawer otherwise.
 */
const CustomerDetailPane = ({
  docked,
  height,
  customer,
  position,
  onPrev,
  onNext,
  onClose,
  onOpenCard,
  onEdit,
  onOpenAddresses,
  onOpenPersonnel,
  onToggleStatus,
}) => {
  const { t, locale } = useTranslation();
  const label = t('customers.panel.label');

  if (!customer) return <DetailPane docked={docked} height={height} open={false} label={label} onClose={onClose} />;

  const isActive = isActiveCustomer(customer);
  const name = displayValue(customer['Başlık']);
  const serviceNumber = text(customer['Müşteri Hizmet Num.']);
  const domestic = text(customer['Yurtiçi mi?']);
  const currency = text(customer['Döviz Tipi']);

  const badges = (
    <>
      <StatusBadge value={text(customer['Durum']) || undefined} />
      {domestic && (
        <Badge tone="info">
          <LuGlobe aria-hidden="true" className="tw-h-3 tw-w-3" />
          {domestic === 'Evet' ? t('customers.domestic') : t('customers.abroad')}
        </Badge>
      )}
      {currency && <Badge tone="neutral">{currency}</Badge>}
    </>
  );

  const actions = [
    { key: 'card', label: t('customers.panel.card'), icon: LuIdCard, tint: 'teal', onClick: onOpenCard },
    { key: 'edit', label: t('customers.panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
    { key: 'addresses', label: t('customers.panel.addresses'), icon: LuMapPin, tint: 'violet', onClick: onOpenAddresses },
    { key: 'personnel', label: t('customers.panel.personnel'), icon: LuUsers, tint: 'indigo', onClick: onOpenPersonnel },
  ];

  const details = [
    { key: 'tax', icon: LuHash, label: t('customers.columns.taxNumber'), value: text(customer['Vergi Numarası']), copyable: true },
    { key: 'agreementNo', icon: LuFileText, label: t('customers.columns.agreementNumber'), value: text(customer['Ana Anlaşma Numarası']), copyable: true },
    { key: 'agreementDate', icon: LuCalendarDays, label: t('customers.columns.agreementDate'), value: formatIsoDate(customer['Ana Anlaşma Tarihi'], locale) },
    { key: 'delivery', icon: LuCalendarRange, label: t('customers.columns.deliveryDate'), value: formatIsoDate(customer['Teslim Tarihi'], locale) },
    {
      key: 'service',
      icon: LuHeadset,
      label: t('customers.columns.serviceNumber'),
      value: serviceNumber,
      href: serviceNumber ? `tel:${serviceNumber.replace(/\s+/g, '')}` : undefined,
      copyable: true,
    },
    { key: 'currency', icon: LuCoins, label: t('customers.columns.currency'), value: currency },
    { key: 'payment', icon: LuWallet, label: t('customers.columns.paymentPeriod'), value: formatPaymentPeriod(t, customer['Ödeme Periyodu']) },
    { key: 'domestic', icon: LuBadgeCheck, label: t('customers.columns.domestic'), value: domestic ? getFilterValueLabel(t, 'Yurtiçi mi?', domestic) : '' },
  ];

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={label}
      heading={t('customers.panel.heading')}
      position={position}
      contentKey={customer.id}
      identity={{
        avatar: customerInitials(customer['Başlık']),
        gradient: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30',
        title: name,
        subtitle: text(customer['Bilgi']) || undefined,
        badges,
      }}
      actions={actions}
      details={details}
      footer={{
        label: isActive ? t('customers.panel.deactivate') : t('customers.panel.activate'),
        icon: isActive ? LuToggleLeft : LuToggleRight,
        tone: isActive ? 'danger' : 'success',
        title: t('customers.panel.statusHint'),
        onClick: onToggleStatus,
      }}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    />
  );
};

CustomerDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  customer: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onOpenCard: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onOpenAddresses: PropTypes.func.isRequired,
  onOpenPersonnel: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
};

export default CustomerDetailPane;
